import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import type { ResidualValueResult } from '@popety-io/types'

const formatNumber = (value: number): string => {
  if (isNaN(value)) return '0'
  return Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, "'")
}

const formatCurrency = (value: number): string => `CHF ${formatNumber(value)}`

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.default};

  @media (max-width: 640px) {
    flex-direction: column;
  }
`

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

const Question = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`

const Headline = styled.div`
  text-align: right;

  @media (max-width: 640px) {
    text-align: left;
  }
`

const HeadlineValue = styled.div<{ $negative?: boolean }>`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme, $negative }) =>
    $negative ? theme.colors.danger : theme.colors.text.primary};
`

const HeadlineSub = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: 2px;
`

const Note = styled.p`
  margin: ${({ theme }) => theme.spacing.default} 0 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

interface ResidualValueSummaryProps {
  residual: ResidualValueResult
}

export const ResidualValueSummary: React.FC<ResidualValueSummaryProps> = ({
  residual,
}) => {
  const { t } = useTranslation('feasibility')
  const negative = residual.residual_value < 0
  const pct = residual.target_profit_pct

  return (
    <Panel>
      <Header>
        <div>
          <Title>{t('residual.title')}</Title>
          <Question>{t('residual.question')}</Question>
        </div>
        <Headline>
          <HeadlineValue $negative={negative}>
            {formatCurrency(residual.residual_value)}
          </HeadlineValue>
          <HeadlineSub>
            {residual.residual_per_m2 != null
              ? t('residual.perM2Value', {
                  value: formatCurrency(residual.residual_per_m2),
                })
              : t('residual.noPlotArea')}
          </HeadlineSub>
        </Headline>
      </Header>
      {negative ? (
        <Note>{t('residual.negativeNote')}</Note>
      ) : (
        <Note>{t('residual.interpretation', { pct })}</Note>
      )}
    </Panel>
  )
}
